import * as Yup from 'yup';

// Khóa cấu hình camera theo API POST /devices/config/camera/:device_id
export const CAMERA_CONFIG_KEYS = {
  forceLive: '360', // Xem live cưỡng bức khi tắt ACC, one-shot (1-300s)
  live4g: '361', // Cho phép live & playback qua 4G (0/1)
  keepLive: '362' // Số giây giữ live sau khi tắt ACC (>= 0)
};

export const FORCE_LIVE_MAX_SECONDS = 300;

// is_allow_camera: 2 = cho phép xem live, 1 = không. Chỉ dùng để hiển thị trạng thái ban đầu của switch 361.
export const isLiveAllowed = isAllowCamera => Number(isAllowCamera) === 2;

// device.config: chuỗi JSON do API danh sách trả về, vd
// {"activate":{"data":{"361":1,"362":500,...}},"camera":{"360":200,"361":1,"362":500},...}
export const parseDeviceConfig = config => {
  if (!config) return {};
  if (typeof config === 'object') return config;

  try {
    const parsed = JSON.parse(config);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (err) {
    return {};
  }
};

// Giá trị camera đã cấu hình: ưu tiên config.camera, fallback config.activate.data
const readStoredCameraValue = (device, key) => {
  const config = parseDeviceConfig(device?.config);
  const value = config.camera?.[key] ?? config.activate?.data?.[key];
  return value === undefined || value === null ? '' : String(value);
};

export const isNonNegativeInteger = value =>
  value !== '' && Number.isInteger(Number(value)) && Number(value) >= 0;

// keepLive = 0 nghĩa là đang tắt giữ live
export const hasKeepLiveValue = value =>
  isNonNegativeInteger(value) && Number(value) > 0;

// *Enabled: switch nhỏ ở mỗi ô — chỉ khi bật mới cho nhập và gửi field đó.
// keepLiveEnabled bật sẵn khi Live 4G đang bật và đã có giá trị keepLive.
export const buildInitialCameraConfig = device => {
  const live4g = isLiveAllowed(device.is_allow_camera) ? 1 : 0;
  const keepLive = device.camera_keep_live ?? '';

  return {
    live4g,
    keepLive,
    keepLiveEnabled: live4g === 1 && hasKeepLiveValue(keepLive),
    forceLive: '',
    forceLiveEnabled: false
  };
};

export const createFuelCalibrationPoint = (percent = '', liters = '') => ({
  percent,
  liters
});

export const buildFuelCalibrationPoints = device => {
  const oilMap = device?.oil_map || [];

  if (!oilMap.length) return [createFuelCalibrationPoint()];

  return oilMap.map(item => ({
    percent: item.l ?? '', // sensor value
    liters: item.v ?? ''
  }));
};

export const normalizeDeviceForConfig = device => {
  if (!device) return {};

  return {
    id: device.id ?? device.device_id ?? null,
    serial: device.serial ?? '',
    name: device.name ?? '',
    is_display_name: device.is_display_name || false,
    status_transmission: device.status_transmission,
    is_turn_on_sound: device.is_turn_on_sound || false,
    license_plate: device.vehicle?.license_plate ?? device.license_plate ?? '',
    vehicle_type_title:
      device.vehicle?.vehicle_type?.title ?? device.vehicle_type ?? '',
    tonnage: device.vehicle?.vehicle_type?.tonnage ?? '',
    slots: device.vehicle?.vehicle_type?.slots ?? '',
    maximum_speed: device.vehicle?.maximum_speed ?? device.max_speed ?? '',
    is_auto_update_maximum_speed:
      device.vehicle?.is_auto_update_maximum_speed ??
      device.is_auto_update_maximum_speed ??
      false,
    device_type_name: device.deviceType?.name ?? device.device_type ?? '',
    acc_status: device.deviceAcc?.acc_status ?? device.acc_status ?? false,
    end_user_phone: device.end_user?.phone ?? device.end_user?.user?.phone ?? null,
    oil_status: Number(device.oil_status) || 0,
    oil_type: device.oil_type,
    oil_map: device.oil_map,
    oil_level: device.oil_level,
    hardware_version: device.hardware_version ?? '',
    sim: device.sim ?? '',
    transport_operator: device.transport_operator ?? '',
    tax_code: device.tax_code ?? '',
    business_id: device.business_id ?? device.business?.id ?? '',
    passenger_capacity: device.passenger_capacity ?? '',
    max_payload: device.max_payload ?? '',
    chassis_number: device.chassis_number ?? '',
    is_allow_camera: device.is_allow_camera,
    camera_keep_live: readStoredCameraValue(device, CAMERA_CONFIG_KEYS.keepLive)
  };
};

export const isTransmissionEnabled = statusTransmission =>
  Number(statusTransmission) === 1;

export const isForceLiveValueValid = value =>
  value !== '' &&
  Number.isInteger(Number(value)) &&
  Number(value) >= 1 &&
  Number(value) <= FORCE_LIVE_MAX_SECONDS;

export const normalizeMaximumSpeed = value => {
  if (value === null || value === undefined) return '';

  const trimmedValue = String(value).trim();
  if (trimmedValue === '') return '';

  return Number(trimmedValue);
};

const CAR_DEVICE_TYPES = ['VGPS_CAR4G', 'VGPS_2CAM', 'VGPS_4CAM'];
const CAMERA_DEVICE_TYPES = ['VGPS_2CAM', 'VGPS_4CAM'];

export const getDeviceCapabilities = deviceTypeName => ({
  isCarType: CAR_DEVICE_TYPES.includes(deviceTypeName),
  isCameraPackageSupported: CAMERA_DEVICE_TYPES.includes(deviceTypeName),
  isOilSensorModelSupported: CAMERA_DEVICE_TYPES.includes(deviceTypeName),
  isMAGType: deviceTypeName === 'VGPS_MAG'
});

export const buildInitialValues = d => ({
  serial: d.serial,
  name: d.name,
  license_plate: d.license_plate,
  version: d.hardware_version,
  sim_phone: d.sim,
  vehicle_type: d.vehicle_type_title,
  fuel_calibration_points: buildFuelCalibrationPoints(d),

  business_name: d.transport_operator,
  tax_code: d.tax_code,
  business_id: d.business_id,
  passenger_capacity: d.passenger_capacity,
  max_payload: d.max_payload,
  chassis_number: d.chassis_number,
  fuelSensorType: d.oil_type ? 'percent' : 'pulse',
  tonnage: d.tonnage,
  slots: d.slots,

  maximum_speed: d.maximum_speed
});

export const buildValidationSchema = ({
  requireTransmitInfo,
  validateFuelCalibration,
  oilType
}) =>
  Yup.object().shape({
    business_name: requireTransmitInfo
      ? Yup.string()
        .trim()
        .required('Tên đơn vị kinh doanh không được để trống')
      : Yup.string().notRequired(),
    tax_code: requireTransmitInfo
      ? Yup.string()
        .trim()
        .required('Mã số thuế không được để trống')
      : Yup.string().notRequired(),
    business_id: requireTransmitInfo
      ? Yup.string().required('Loại hình kinh doanh không được để trống')
      : Yup.string().notRequired(),

    fuel_calibration_points: validateFuelCalibration
      ? Yup.array()
        .of(
          Yup.object().shape({
            percent: Yup.number()
              .required('Vui lòng nhập giá trị cảm biến')
              .min(0, 'Giá trị không hợp lệ')
              .when('$fuelSensorType', (fuelSensorType, schema) =>
                oilType === 'pulse'
                  ? schema.max(4095, 'Giá trị tối đa là 4095')
                  : schema.max(100, 'Giá trị tối đa là 100%')
              ),
            liters: Yup.number()
              .required('Vui lòng nhập dung tích')
              .min(0, 'Dung tích không hợp lệ')
          })
        )
        .test(
          'min-2-pairs',
          'Vui lòng cấu hình ít nhất 2 cặp giá trị',
          value => !value || value.length === 0 || value.length >= 2
        )
      : Yup.array().notRequired()
  });

// Payload cho updateDevice (nút "Lưu lại")
export const buildUpdateDevicePayload = (
  data,
  { isTransmit }
) => {
  const oil_map = data.fuel_calibration_points
    .filter(p => p.percent !== '' && p.liters !== '')
    .map(p => ({
      l: Number(p.percent),
      v: Number(p.liters)
    }));

  return {
    vehicle_license_plate: '',
    title_device_type: data.vehicle_type,
    slots: data.slots || null,
    tonnage: data.tonnage || null,
    name_of_user_for_gps: null,
    version: data.version,
    sim_phone: data.sim_phone,

    business_id: isTransmit && data?.business_id ? data.business_id : null,
    passenger_capacity:
      isTransmit && data?.passenger_capacity
        ? Number(data.passenger_capacity)
        : null,
    tax_code: isTransmit && data?.tax_code ? data.tax_code : null,
    max_payload:
      isTransmit && data?.max_payload ? Number(data.max_payload) : null,
    transport_operator:
      isTransmit && data?.business_name ? data.business_name : null,
    chassis_number:
      isTransmit && data?.chassis_number ? data?.chassis_number : null,
    oil_type: data?.fuelSensorType === 'percent' ? 1 : 0,
    oil_map: oil_map.length > 0 ? oil_map : null
  };
};
